import React, { useState, useRef } from 'react';
import {
    Alert,
    Box,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    OutlinedInput,
    Stack,
    Typography,
    IconButton,
    Paper,
    TextField,
    Grid
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { styled } from '@mui/material/styles';
import { EquipmentRequestEntity, FileStoreEntity } from 'graphql/graphQlApiHooks';
import UploaderService from 'services/UplodaService';
import { useSnackbar } from 'notistack';
import Iconify from 'components/Iconify';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

interface Props {
    equipmentRequest: EquipmentRequestEntity;
    onClose: () => void;
    onAccept: (acceptanceCertificateId: string, quantity: number, name?: string) => void;
    loading?: boolean;
}

export const SingleEquipmentAcceptanceDialog: React.FC<Props> = ({
    equipmentRequest,
    onClose,
    onAccept,
    loading = false
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const fileInputRef = useRef<HTMLInputElement>(null);
    
    const [acceptanceFile, setAcceptanceFile] = useState<FileStoreEntity | null>(null);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [equipmentName, setEquipmentName] = useState(equipmentRequest.name);
    const [acceptedQuantity, setAcceptedQuantity] = useState(equipmentRequest.count || 0);

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        setUploadingFile(true);
        try {
            const uploadedFiles = await UploaderService.uploadFiles(event as any, {
                onUploadProgress: (progressEvent: any) => {
                }
            });
            if (uploadedFiles && uploadedFiles.length > 0) {
                setAcceptanceFile(uploadedFiles[0]);
            }
        } catch (error) {
            enqueueSnackbar('Ошибка при загрузке файла', { variant: 'error' });
            console.error('File upload error:', error);
        } finally {
            setUploadingFile(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleRemoveFile = () => {
        setAcceptanceFile(null);
    };

    const handleSubmit = async () => {
        try {
            const isNameChanged = equipmentRequest.name !== equipmentName;
            
            onAccept(
                acceptanceFile?.id || null,
                acceptedQuantity,
                isNameChanged ? equipmentName : undefined
            );
            enqueueSnackbar('Оборудование принято', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar(`Ошибка при обработке заявки: ${error?.message}`, { variant: "error" });
            console.error("Ошибка при обработке заявки", error);
        }
    };

    return (
        <>
            <DialogTitle>
                <Typography variant="h4" component="h1" fontWeight="bold">
                    Приемка оборудования
                </Typography>
                <Typography className='text-sm' variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Заявка: {equipmentRequest.name}
                </Typography>
            </DialogTitle>

            <DialogContent>
                <Stack spacing={3}>
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Акт приемки оборудования 
                        </Typography>
                        
                        {!acceptanceFile ? (
                            <Button
                                component="label"
                                variant="outlined"
                                startIcon={<Iconify icon="material-symbols:upload" />}
                                disabled={uploadingFile}
                                fullWidth
                                sx={{ py: 2 }}
                            >
                                {uploadingFile ? 'Загрузка...' : 'Загрузить акт приемки'}
                                <VisuallyHiddenInput
                                    ref={fileInputRef}
                                    type="file"
                                    onChange={handleFileUpload}
                                />
                            </Button>
                        ) : (
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Iconify icon="material-symbols:description" />
                                        <Typography variant="body2">
                                            {acceptanceFile.name}
                                        </Typography>
                                    </Box>
                                    <IconButton 
                                        size="small" 
                                        onClick={handleRemoveFile}
                                        color="error"
                                    >
                                        <Iconify icon="material-symbols:delete" />
                                    </IconButton>
                                </Box>
                            </Paper>
                        )}
                    </Box>

                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Детали принимаемого оборудования
                        </Typography>
                        
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    label="Наименование оборудования"
                                    value={equipmentName}
                                    onChange={(e) => setEquipmentName(e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                />
                            </Grid>
                            
                            <Grid item xs={6}>
                                <TextField
                                    label="Заявленное количество"
                                    value={equipmentRequest.count || 0}
                                    disabled
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                />
                            </Grid>
                            
                            <Grid item xs={6}>
                                <FormControl fullWidth size="small">
                                    <TextField
                                        label="Принятое количество"
                                        value={acceptedQuantity}
                                        onChange={(e) => setAcceptedQuantity(parseInt(e.target.value) || 0)}
                                        type="number"
                                        inputProps={{ min: 0 }}
                                        variant="outlined"
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>

                    <Alert severity="info">
                        Загрузите акт приемки оборудования, укажите фактическое количество принятого оборудования.
                    </Alert>
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Отмена
                </Button>
                <LoadingButton
                    onClick={handleSubmit}
                    loading={loading}
                    variant="contained"
                    disabled={acceptedQuantity <= 0}
                >
                    Подтвердить принятие
                </LoadingButton>
            </DialogActions>
        </>
    );
};